import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import Modal from 'modules/views/shared/Modal';
import { ReactComponent as ExclamationRound } from 'assets/images/ExclamationRound.svg';

import { removeWorker } from 'utils/indexedDB';
import { prettifyErrorResponse } from 'utils';
import { IOnboardingState } from 'modules/state-mgmt/onboarding/state';
import { useStylesDeleteWorkerModal } from '../styles';

interface IDeleteWorkerModalProps {
  worker: IOnboardingState;
  onClose: () => void;
  onDeleteSuccess: () => void;
}

const DeleteWorkerModal = ({ worker, onClose, onDeleteSuccess }: IDeleteWorkerModalProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();
  const classes = useStylesDeleteWorkerModal();

  const onDeleteCertification = async () => {
    setIsDeleting(true);
    try {
      await removeWorker(worker?.db?.id);
      onDeleteSuccess();
      setIsDeleting(false);
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      isVisible={true}
      modalStyleClass={classes.modal}
      modalTitleStyleClass={classes.modalTitle}
      modalContentStyleClass={classes.modalContent}
      modalActionsStyleClass={classes.modalActions}
      testId="delete-worker-modal"
      Content={
        <>
          <div className={classes.imageParent}>
            <ExclamationRound />
          </div>
          <p className={classes.title}>{t('workerStatus.deleteWorkerModal.title')}</p>
          <p className={classes.warningText}>{t('workerStatus.deleteWorkerModal.message')}</p>
        </>
      }
      actionButtons={[
        {
          label: t('common.cancel'),
          variant: 'outlined',
          styleClass: classes.button,
          testId: 'delete-worker-cancel-button',
          onClick: onClose,
        },
        {
          label: t('common.delete'),
          styleClass: classes.button,
          testId: 'delete-worker-confirm-button',
          isLoading: isDeleting,
          onClick: onDeleteCertification,
        },
      ]}
      onClose={onClose}
    />
  );
};

export default React.memo(DeleteWorkerModal);
